import React from "react"
import { Helmet } from "react-helmet"
import Layout from "../components/layout"
import BookShowcase from "../components/book-showcase"
import BookPreviewModal from "../components/book-preview-modal"
import couvertureAlfredGrevin from "../images/couverture-livre.jpg"
import couvertureTabet from "../images/books/tabet.webp"
import couvertureGiffey from "../images/books/giffey.webp"
import couvertureCapy from "../images/books/capy.webp"
import couvertureClave from "../images/books/clave.webp"
import couvertureForestPoivet from "../images/books/forest-poivet.webp"
import * as booksCss from "./modules/livres.module.scss"

const featuredBook = {
  title: "Alfred Grévin",
  period: "1827-1892",
  status: "Disponible",
  cover: couvertureAlfredGrevin,
  coverAlt: "Couverture du livre consacré à l'œuvre d'Alfred Grévin",
  description:
    "Un livre pour découvrir ses dessins humoristiques, ses costumes de théâtre et ses créations pour le Musée Grévin.",
  details: [
    { label: "Format", value: "120 pages illustrées" },
    { label: "Édition normale", value: "22 €" },
  ],
  href: "/livre",
  ctaLabel: "Découvrir le livre",
}

const archiveBooks = [
  {
    title: "Giorgio Tabet & Enzo Magni",
    period: "1904-2002 et 1914-1981",
    cover: couvertureTabet,
    coverAlt: "Couverture du livre consacré à Giorgio Tabet et Enzo Magni",
    description:
      "Un ouvrage consacré au roman-en-images dessiné par Giorgio Tabet vers 1950, avec Enzo Magni.",
    details: [{ label: "Sujet", value: "Roman-en-images, vers 1950" }],
  },
  {
    title: "René Giffey",
    period: "1884-1965",
    cover: couvertureGiffey,
    coverAlt: "Couverture du livre consacré à René Giffey",
    description: "Un ouvrage illustré consacré à l'œuvre de René Giffey.",
    details: [],
  },
  {
    title: "Marcel Capy",
    period: "1865-1941",
    cover: couvertureCapy,
    coverAlt: "Couverture du livre consacré à Marcel Capy",
    description: "Un ouvrage illustré consacré à l'œuvre de Marcel Capy.",
    details: [],
  },
  {
    title: "Antoni Clavé",
    period: "1913-2005",
    cover: couvertureClave,
    coverAlt: "Couverture du livre consacré à Antoni Clavé",
    description:
      "Un ouvrage consacré au travail d'Antoni Clavé comme dessinateur de bande dessinée.",
    details: [{ label: "Sujet", value: "Bande dessinée" }],
  },
  {
    title: "Jean-Claude Forest & Raymond Poïvet",
    cover: couvertureForestPoivet,
    coverAlt:
      "Couverture du livre consacré à Jean-Claude Forest et Raymond Poïvet",
    description:
      "Un ouvrage illustré consacré aux créations de Jean-Claude Forest et Raymond Poïvet.",
    details: [],
  },
]

const Reveal = ({ children, className = "" }) => {
  const elementRef = React.useRef(null)
  const [isVisible, setIsVisible] = React.useState(false)

  React.useEffect(() => {
    const element = elementRef.current

    if (!element || !("IntersectionObserver" in window)) {
      setIsVisible(true)
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.12 }
    )

    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={elementRef}
      className={`${booksCss.reveal} ${
        isVisible ? booksCss.revealVisible : ""
      } ${className}`}
    >
      {children}
    </div>
  )
}

const BooksPage = () => {
  const [selectedBook, setSelectedBook] = React.useState(null)

  const closePreview = () => {
    setSelectedBook(null)
  }

  return (
    <Layout>
      <Helmet>
        <title>Les livres | Galerie Alfred Grévin</title>
        <meta
          name="description"
          content="Découvrez les livres et ouvrages illustrés présentés par la Galerie Alfred Grévin."
        />
      </Helmet>

      <main className={booksCss.page}>
        <section className={booksCss.library} aria-labelledby="library-title">
          <Reveal className={booksCss.libraryHeading}>
            <p>Collection éditoriale</p>
            <h2 id="library-title">Les ouvrages</h2>
            <span>
              Chaque livre ouvre une porte différente sur l'histoire de
              l'illustration, du spectacle et de la création graphique.
            </span>
          </Reveal>

          <div className={booksCss.libraryGrid}>
            <Reveal>
              <BookShowcase book={featuredBook} />
            </Reveal>

            <div className={booksCss.archiveGrid}>
              {archiveBooks.map(book => (
                <Reveal key={book.title}>
                  <button
                    className={booksCss.miniBook}
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={`Agrandir la couverture de ${book.title}`}
                    onClick={() => setSelectedBook(book)}
                  >
                    <div className={booksCss.miniBookVisual}>
                      <img
                        src={book.cover}
                        alt={book.coverAlt}
                        loading="lazy"
                      />
                    </div>
                    <h3>{book.title}</h3>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <Reveal>
          <aside className={booksCss.closing}>
            <div>
              <p className={booksCss.closingEyebrow}>Regards d'artistes</p>
              <h2>Six ouvrages, autant d'univers graphiques.</h2>
              <p>
                De Alfred Grévin à Jean-Claude Forest et Raymond Poïvet, cette
                sélection rassemble les artistes étudiés et défendus par la
                galerie.
              </p>
            </div>
          </aside>
        </Reveal>

        <BookPreviewModal book={selectedBook} onClose={closePreview} />
      </main>
    </Layout>
  )
}

export default BooksPage
